import api from "@/api/client";
import {
    Autocomplete,
    TextField,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import debounce from "lodash.debounce";
import {
    useCallback,
    useEffect,
    useState,
    type SyntheticEvent,
} from "react";

// --- INTERFACES ---

export interface StreetResult {
    id: number;
    thoroughfare: string;
    post_town: string | null;
    postcodes: string[];
    address_count: number;
}

/**
 * UPDATED PROPS: Removed the postcodes setter, added a single select handler.
 */
interface StreetAutocompleteProps {
    streetInput: string;
    onStreetSelect: (street: StreetResult | null) => void;
    setStreetInput: (v: string) => void; // Keep this setter for typing/searching
    enableAutocomplete?: boolean; // allow typing but block fetching/popup when false
    onEnter?: () => void;
}

// --- UTILITY FUNCTION ---

function formatPostcodes(postcodes: string[]): string {
  if (postcodes.length <= 10) {
    return postcodes.join(", ");
  }

  const prefixes = Array.from(new Set(
    postcodes.map((pc) => pc.split(" ")[0])
  ));

  return prefixes.join(", ");
}

// --- COMPONENT ---

export default function StreetAutocomplete({
    streetInput,
    onStreetSelect, // New handler prop
    setStreetInput, // Retained for handling user typing
    enableAutocomplete = true,
    onEnter,
}: StreetAutocompleteProps) {
    const [options, setOptions] = useState<StreetResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState<StreetResult | string | null>(null);
    
    // State to hold the currently selected *object* for Autocomplete's internal value
    const [selectedStreet, setSelectedStreet] = useState<StreetResult | null>(null);

    // Keep the selected object in sync when the parent clears the input
    useEffect(() => {
        if (streetInput === "" && selectedStreet) {
            setSelectedStreet(null);
        }
    }, [streetInput, selectedStreet]);

    /**
     * Debounced API call
     */
    const fetchStreets = useCallback(
        debounce(async (query: string) => {
            if (!enableAutocomplete) {
                setOptions([]);
                return;
            }

            if (!query || query.length < 2) {
                setOptions([]);
                return;
            }

            setLoading(true);
            try {
                const res = await api.get<StreetResult[]>("/streets/autocomplete/", {
                    params: { q: query }
                });
                
                const data: StreetResult[] = res.data;
                setOptions(data);
            } catch (err) {
                console.error("Street autocomplete failed:", err);
            } finally {
                setLoading(false);
            }
        }, 250),
        [enableAutocomplete]
    );

    useEffect(() => {
        if (!enableAutocomplete) {
            setOpen(false);
            setLoading(false);
            setOptions([]);
        }
    }, [enableAutocomplete]);

    /**
     * Input text change (user typing)
     */
    const handleInputChange = (
        _event: SyntheticEvent,
        value: string,
    ) => {
        // If the user starts typing, we clear the selected object
        if (selectedStreet && value !== selectedStreet.thoroughfare) {
            setSelectedStreet(null);
        }

        setStreetInput(value);
        if (!enableAutocomplete) {
            setOptions([]);
            return;
        }

        if (value.trim() === "") {
            setOptions([]);
            return;
        }
        fetchStreets(value);
    };

    /**
     * Simplified selection handler
     * This function now only manages internal state and calls the parent handler.
     */
    const handleSelect = (
        _event: SyntheticEvent,
        street: StreetResult | string | null,
    ) => {
        if (street === null) {
            setSelectedStreet(null);
            onStreetSelect(null);
            return;
        }

        if (typeof street === "string") {
            setSelectedStreet(null);
            setStreetInput(street);
            return;
        }

        setSelectedStreet(street); 
        setStreetInput(street ? street.thoroughfare || "" : "");
        onStreetSelect(street);
    };

    return (
        <Autocomplete<StreetResult, false, false, true>
            value={selectedStreet} 
            freeSolo
            inputValue={streetInput}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            options={options}
            open={enableAutocomplete ? open : false}
            onOpen={() => enableAutocomplete && setOpen(true)}
            onClose={() => setOpen(false)}
            onHighlightChange={(_event, option) => setHighlighted(option)}
            filterOptions={(x) => x}
            loading={loading}

            getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.thoroughfare ?? ""
            }
            getOptionKey={(opt) => typeof opt === "string" ? opt : String(opt.id)}
            isOptionEqualToValue={(a, b) => {
                if (typeof b === "string") {
                    return a.thoroughfare === b;
                }
                return a.id === b.id;
            }}

            // HEIGHT/LAYOUT: Stable slotProps approach
            slotProps={{
                popper: {
                    sx: {
                        maxHeight: "calc(100vh - 180px)",
                        overflowY: "auto",
                    },
                },
                paper: {
                    sx: {
                        maxHeight: "inherit",
                        overflowY: "auto",
                    },
                },
            }}
            
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Street"
                    size="small"
                    fullWidth
                    data-lpignore="true"
                    inputProps={{
                        ...params.inputProps,
                        autoCapitalize: "none",
                        autoCorrect: "off",
                        spellCheck: false,
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            // Let Autocomplete select the highlighted option; otherwise trigger parent onEnter.
                            if (highlighted) return;
                            e.preventDefault();
                            onEnter?.();
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    
                />
            )}
            
            // LEFT-ALIGNMENT & SPACING
            renderOption={(props, street) => {
                const { key, ...liProps } = props; 
                
                return (
                    <li {...liProps} key={key} style={{ padding: 0 }}> 
                        <Box
                            sx={{
                                width: '100%', 
                                display: "flex", 
                                flexDirection: "column",
                                alignItems: "flex-start", 
                                py: 0.5, 
                                px: 2, 
                            }}
                        >
                            <Typography variant="body2" component="span" noWrap>
                                {street.thoroughfare}
                                {street.post_town ? `, ${street.post_town}` : ""}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" component="span" noWrap>
                                {street.address_count} addresses — {formatPostcodes(street.postcodes)}
                            </Typography>
                        </Box>
                    </li>
                );
            }}
        />
    )
}

// import api from "@/api/client";
// import {
//     Autocomplete,
//     TextField,
//     Box,
//     Typography,
//     CircularProgress,
// } from "@mui/material";
// import debounce from "lodash.debounce";
// import {
//     useCallback,
//     useState,
//     type SyntheticEvent,
// } from "react";

// // --- INTERFACES ---

// export interface StreetResult {
//     id: number;
//     thoroughfare: string;
//     post_town: string | null;
//     postcodes: string[];
//     address_count: number;
// }

// interface StreetAutocompleteProps {
//     streetInput: string;
//     handleStreetSelect: (v: StreetResult) => void;
// }

// // --- UTILITY FUNCTION ---

// function formatPostcodes(postcodes: string[]): string {
//   if (postcodes.length <= 10) {
//     return postcodes.join(", ");
//   }

//   const prefixes = Array.from(new Set(
//     postcodes.map((pc) => pc.split(" ")[0])
//   ));

//   return prefixes.join(", ");
// }

// // --- COMPONENT ---

// export default function StreetAutocomplete({
//     streetInput,
//     handleStreetSelect
// }: StreetAutocompleteProps) {
//     const [options, setOptions] = useState<StreetResult[]>([]);
//     const [loading, setLoading] = useState(false);
    
//     // State to hold the currently selected *object* for Autocomplete's internal value
//     const [selectedStreet, setSelectedStreet] = useState<StreetResult | null>(null);

//     /**
//      * Debounced API call
//      */
//     const fetchStreets = useCallback(
//         debounce(async (query: string) => {
//             if (!query || query.length < 2) {
//                 setOptions([]);
//                 return;
//             }

//             setLoading(true);
//             try {
//                 const res = await api.get<StreetResult[]>("/streets/autocomplete/", {
//                     params: { q: query }
//                 });
                
//                 const data: StreetResult[] = res.data;
//                 setOptions(data);
//             } catch (err) {
//                 console.error("Street autocomplete failed:", err);
//             } finally {
//                 setLoading(false);
//             }
//         }, 250),
//         []
//     );

//     /**
//      * Input text change (user typing)
//      */
//     const handleInputChange = (
//         _event: SyntheticEvent,
//         value: string,
//     ) => {
//         // If the user starts typing, we clear the selected object
//         if (selectedStreet && value !== selectedStreet.thoroughfare) {
//              setSelectedStreet(null);
//         }

//         setStreetInput(value);
//         if (value.trim() === "") {
//             setOptions([]); 
//             return;
//         }
//         fetchStreets(value);
//     };

//     // /**
//     //  * Choosing an option (user selection)
//     //  */
//     // const handleSelect = (
//     //     _event: SyntheticEvent,
//     //     street: StreetResult | null
//     // ) => {
//     //     // Store the selected object for the Autocomplete's 'value' prop
//     //     setSelectedStreet(street); 

//     //     if (!street) {
//     //         setStreetInput("");
//     //         setPostcodeInput("");
//     //         return;
//     //     }

//     //     // Propagate ONLY the thoroughfare to the parent state, controlling the input string
//     //     setStreetInput(street.thoroughfare || "");
//     //     setPostcodeInput(street.postcodes.join(", "));
//     // };

//     return (
//         <Autocomplete<StreetResult, false, false, false>
//             value={selectedStreet} 
//             freeSolo={false}
//             inputValue={streetInput}
//             onInputChange={handleInputChange}
//             onChange={handleStreetSelect}
//             options={options}
//             filterOptions={(x) => x}
//             loading={loading}
            
//             // --- FIX: Revert getOptionLabel to thoroughfare only ---
//             getOptionLabel={(opt) => opt.thoroughfare ?? ""} // Displays ONLY the street name
//             getOptionKey={(opt) => opt.id} // Retain unique key for stability
//             isOptionEqualToValue={(a, b) => a.id === b.id}

//             // HEIGHT/LAYOUT: Stable slotProps approach
//             slotProps={{
//                 popper: {
//                     sx: {
//                         maxHeight: "calc(100vh - 180px)",
//                         overflowY: "auto",
//                     },
//                 },
//                 paper: {
//                     sx: {
//                         maxHeight: "inherit",
//                         overflowY: "auto",
//                     },
//                 },
//             }}
            
//             renderInput={(params) => (
//                 <TextField
//                     {...params}
//                     label="Street"
//                     size="small"
//                     fullWidth
//                     data-lpignore="true"
//                     inputProps={{
//                         ...params.inputProps,
//                         autoCapitalize: "none",
//                         autoCorrect: "off",
//                         spellCheck: false,
//                     }}
//                     InputProps={{
//                         ...params.InputProps,
//                         endAdornment: (
//                             <>
//                                 {loading ? <CircularProgress size={18} /> : null}
//                                 {params.InputProps.endAdornment}
//                             </>
//                         ),
//                     }}
                    
//                 />
//             )}
            
//             // LEFT-ALIGNMENT & SPACING (Displaying town/details only in the dropdown)
//             renderOption={(props, street) => {
//                 const { key, ...liProps } = props; 
                
//                 return (
//                     <li {...liProps} key={key} style={{ padding: 0 }}> 
//                         <Box
//                             sx={{
//                                 width: '100%', 
//                                 display: "flex", 
//                                 flexDirection: "column",
//                                 alignItems: "flex-start", 
//                                 py: 0.5, 
//                                 px: 2, 
//                             }}
//                         >
//                             <Typography variant="body2" component="span" noWrap>
//                                 {street.thoroughfare}
//                                 {/* The full detail (including town) is correctly visible here in the dropdown */}
//                                 {street.post_town ? `, ${street.post_town}` : ""}
//                             </Typography>

//                             <Typography variant="caption" color="text.secondary" component="span" noWrap>
//                                 {street.address_count} addresses — {formatPostcodes(street.postcodes)}
//                             </Typography>
//                         </Box>
//                     </li>
//                 );
//             }}
//         />
//     )
// }

// // import api from "@/api/client";
// // import {
// //     Autocomplete,
// //     TextField,
// //     Box,
// //     Typography,
// //     CircularProgress,
// // } from "@mui/material";
// // import debounce from "lodash.debounce";
// // import {
// //     useCallback,
// //     useState,
// //     type SyntheticEvent,
// // } from "react";

// // // --- INTERFACES ---

// // /**
// //  * API result from /api/streets/autocomplete
// //  */
// // export interface StreetResult {
// //     id: number;
// //     thoroughfare: string;
// //     post_town: string | null;
// //     postcodes: string[];
// //     address_count: number;
// // }

// // /**
// //  * Props for the StreetAutocomplete component
// //  */
// // interface StreetAutocompleteProps {
// //     streetInput: string;
// //     setStreetInput: (v: string) => void;
// //     setPostcodeInput: (v: string) => void;
// // }

// // // --- UTILITY FUNCTION ---

// // function formatPostcodes(postcodes: string[]): string {
// //   if (postcodes.length <= 10) {
// //     return postcodes.join(", ");
// //   }

// //   // Extract prefixes (before the space): "BN3 1AB" → "BN3"
// //   const prefixes = Array.from(new Set(
// //     postcodes.map((pc) => pc.split(" ")[0])
// //   ));

// //   return prefixes.join(", ");
// // }

// // // --- COMPONENT ---

// // export default function StreetAutocomplete({
// //     streetInput,
// //     setStreetInput,
// //     setPostcodeInput,
// // }: StreetAutocompleteProps) {
// //     const [options, setOptions] = useState<StreetResult[]>([]);
// //     const [loading, setLoading] = useState(false);
// //     const [selectedStreet, setSelectedStreet] = useState<StreetResult | null>(null);

// //     /**
// //      * Debounced API call
// //      */
// //     const fetchStreets = useCallback(
// //         debounce(async (query: string) => {
// //             if (!query || query.length < 2) {
// //                 setOptions([]);
// //                 return;
// //             }

// //             setLoading(true);
// //             try {
// //                 const res = await api.get<StreetResult[]>("/streets/autocomplete/", {
// //                     params: { q: query }
// //                 });
                
// //                 const data: StreetResult[] = res.data;
// //                 setOptions(data);
// //             } catch (err) {
// //                 console.error("Street autocomplete failed:", err);
// //             } finally {
// //                 setLoading(false);
// //             }
// //         }, 250),
// //         []
// //     );

// //     /**
// //      * Input text change
// //      */
// //     const handleInputChange = (
// //         _event: SyntheticEvent,
// //         value: string,
// //     ) => {
// //         setStreetInput(value);
// //         if (value.trim() === "") {
// //             setOptions([]); // clear old results
// //             return;
// //         }
// //         fetchStreets(value);
// //     };

// //     /**
// //      * Choosing an option
// //      */
// //     // const handleSelect = (
// //     //     _event: SyntheticEvent,
// //     //     street: StreetResult | null
// //     // ) => {
// //     //     if (!street) return;

// //     //     setStreetInput(street.thoroughfare || "");
// //     //     setPostcodeInput(street.postcodes.join(", "));
// //     // };

// //     const handleSelect = (
// //     _event: SyntheticEvent,
// //     street: StreetResult | null
// // ) => {
// //     setSelectedStreet(street); // <-- NEW: Store the selected object

// //     if (!street) {
// //         // Handle clearing/null selection
// //         setStreetInput("");
// //         setPostcodeInput("");
// //         return;
// //     }

// //     setStreetInput(street.thoroughfare || "");
// //     setPostcodeInput(street.postcodes.join(", "));
// // };

// // // value={options.find((o) => o.thoroughfare === streetInput) ?? null}
// //     return (
// //         <Autocomplete<StreetResult, false, false, false>
            
// //             value={selectedStreet}
// //             inputValue={streetInput}
// //             onInputChange={handleInputChange}
// //             onChange={handleSelect}
// //             options={options}
// //             filterOptions={(x) => x}
// //             loading={loading}
            
// //             // --- CORE FIXES ---
// //             // 1. IMPROVED LABEL: Include town for better user context and differentiation
// //             getOptionLabel={(opt) => 
// //                 opt.thoroughfare + (opt.post_town ? `, ${opt.post_town}` : "")
// //             }
// //             // 2. KEY FIX: Use the unique ID to prevent duplicate key warnings
// //             getOptionKey={(opt) => opt.id}
// //             isOptionEqualToValue={(a, b) => a.id === b.id}

// //             // --- HEIGHT/LAYOUT FIX: Use slotProps for reliable max height ---
// //             slotProps={{
// //                 popper: {
// //                     sx: {
// //                         // Max height is 100vh MINUS a safe amount to account for bars and input.
// //                         // (Header 56px + Input 40px + BottomNav 64px + Padding ~20px = ~180px)
// //                         maxHeight: "calc(100vh - 180px)",
// //                         overflowY: "auto",
// //                     },
// //                 },
// //                 paper: {
// //                     sx: {
// //                         maxHeight: "inherit",
// //                         overflowY: "auto",
// //                     },
// //                 },
// //             }}
            
// //             renderInput={(params) => (
// //                 <TextField
// //                     {...params}
// //                     label="Street"
// //                     size="small"
// //                     fullWidth
// //                     data-lpignore="true"
// //                     inputProps={{
// //                         ...params.inputProps,
// //                         autoCapitalize: "none",
// //                         autoCorrect: "off",
// //                         spellCheck: false,
// //                     }}
// //                     InputProps={{
// //                         ...params.InputProps,
// //                         endAdornment: (
// //                             <>
// //                                 {loading ? <CircularProgress size={18} /> : null}
// //                                 {params.InputProps.endAdornment}
// //                             </>
// //                         ),
// //                     }}
                    
// //                 />
// //             )}
            
// //             // --- LEFT-ALIGNMENT & SPACING FIX ---
// //             renderOption={(props, street) => {
// //                 const { key, ...liProps } = props; 
                
// //                 return (
// //                     // Apply required LI props including the now guaranteed unique key
// //                     <li {...liProps} key={key} style={{ padding: 0 }}> 
// //                         <Box
// //                             sx={{
// //                                 width: '100%', 
// //                                 display: "flex", 
// //                                 flexDirection: "column",
// //                                 alignItems: "flex-start", 
// //                                 py: 0.5, // Reduced vertical padding for compact options
// //                                 px: 2, 
// //                             }}
// //                         >
// //                             <Typography variant="body2" component="span" noWrap>
// //                                 {street.thoroughfare}
// //                                 {street.post_town ? `, ${street.post_town}` : ""}
// //                             </Typography>

// //                             <Typography variant="caption" color="text.secondary" component="span" noWrap>
// //                                 {street.address_count} addresses — {formatPostcodes(street.postcodes)}
// //                             </Typography>
// //                         </Box>
// //                     </li>
// //                 );
// //             }}
// //         />
// //     )
// // }
